// Thin wrapper around `fetch` for the agent app's calls to the openline server.
//
// Resolves the base URL via `serverOrigin()` so `VITE_AUTH_SERVER_URL=auto`
// (or unset) derives from `window.location.hostname` at runtime. That lets
// a teammate visiting http://10.2.2.9:5175 talk to http://10.2.2.9:4000
// without anyone rebuilding the bundle. When an access token is
// available it's attached as a Bearer header.
//
// The token getter is passed in by the store so we don't create a circular
// import between this file and `stores/agent.ts`. The store also installs a
// refresh hook (`setTokenRefresher`) so apiFetch can transparently recover
// from a 401 by refreshing the access token once and retrying.

import { serverOrigin } from './server-origin';

const BASE = serverOrigin();

let accessTokenProvider: () => string | null = () => null;
let tokenRefresher: (() => Promise<boolean>) | null = null;
// One global in-flight refresh promise — multiple parallel 401s should not
// kick off multiple refresh calls (the second would race against the first
// and fail because the refresh token rotates on every successful refresh).
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessTokenProvider(fn: () => string | null): void {
  accessTokenProvider = fn;
}

/** Install the function apiFetch should call when it gets a 401. Returns
 *  true if a fresh access token is now available, false to give up. */
export function setTokenRefresher(fn: (() => Promise<boolean>) | null): void {
  tokenRefresher = fn;
}

// Called when a 401 is UNRECOVERABLE for a protected request (refresh failed, or
// even a freshly-refreshed token was rejected). The app installs this to end the
// session and bounce to /login with a message, rather than leaving the agent on
// a screen whose data silently fails to load.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

// Auth endpoints own their 401s (bad credentials / expired refresh token); a
// 401 there must NOT trigger the global "session expired" redirect/loop.
function isAuthPath(path: string): boolean {
  return path.startsWith('/api/auth/');
}

export interface ApiError {
  status: number;
  body: unknown;
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  // First attempt with whatever token we have.
  const first = await doFetch(path, init);
  if (first.ok || first.status !== 401) {
    return finalize<T>(first);
  }
  // 401 below. Auth endpoints handle their own 401s; never refresh-loop or
  // trigger the global session-expired redirect from them.
  if (isAuthPath(path)) {
    return finalize<T>(first);
  }
  // A protected request 401'd. If we can't refresh, the session is over.
  if (!tokenRefresher) {
    onUnauthorized?.();
    return finalize<T>(first);
  }
  // De-dupe parallel refreshes.
  if (!refreshInFlight) {
    refreshInFlight = tokenRefresher().finally(() => {
      refreshInFlight = null;
    });
  }
  const refreshed = await refreshInFlight;
  if (!refreshed) {
    // Refresh token also expired/revoked — end the session.
    onUnauthorized?.();
    return finalize<T>(first);
  }
  // Retry once with the new token. If even that is rejected, the session can't
  // be salvaged — bounce to login rather than surfacing a silent failure.
  const second = await doFetch(path, init);
  if (second.status === 401) {
    onUnauthorized?.();
  }
  return finalize<T>(second);
}

async function doFetch(
  path: string,
  init: RequestInit,
): Promise<{ ok: boolean; status: number; body: unknown }> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = accessTokenProvider();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  return { ok: res.ok, status: res.status, body };
}

function finalize<T>(result: { ok: boolean; status: number; body: unknown }): T {
  if (!result.ok) {
    const err: ApiError = { status: result.status, body: result.body };
    throw err;
  }
  return result.body as T;
}

export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'status' in err && 'body' in err;
}
